"use client";

import { Product } from "@repo/apis/browse-search-v2";
import { RecommendationProduct } from "@repo/apis/recommendations";
import { Box } from "@repo/ui/box";
import * as Flex from "@repo/ui/flex";
import * as List from "@repo/ui/list";
import * as ProductTile from "@repo/ui/product-tile";
import { ImageSizes } from "@repo/utils/image-presets";
import { prependUrlWithHttps } from "@repo/utils/prepend-url-with-https";
import { CTA_REF_KEY } from "@/constants";
import { ProductLink } from "../product-link";
import classes from "./product-carousel.module.css";

export type ProductCarouselProps = {
  products: Product[] | RecommendationProduct[] | null | undefined;
  store: string;
  country: string;
  sourceSetProfile: ImageSizes;
  productTitleClassName?: string;
  productPriceClassName?: string;
  showProductInfo?: boolean;
  listItemStyle?: React.CSSProperties;
  currency: string;
  overlayProductInfo?: boolean;
  type?: "pab" | "ymal";
  onProductClick?: () => void;
  wrapper?: React.ComponentType<{ children: React.ReactElement }>;
};

export const ProductCarousel = ({
  products,
  country,
  sourceSetProfile,
  productTitleClassName,
  productPriceClassName,
  listItemStyle,
  currency,
  showProductInfo = true,
  overlayProductInfo = false,
  type,
  onProductClick,
  wrapper: Wrapper,
}: ProductCarouselProps) => {
  if (!products || products.length < 4) return null;

  const listItems = products.map((product) => {
    const productLink = (
      <ProductLink
        productName={product.name}
        productId={product.id}
        fragments={type && { [CTA_REF_KEY]: type }}
        onClick={onProductClick}
        className={classes.productLink}
      >
        <ProductTile.ProductImage
          imageUrl={prependUrlWithHttps(product.imageUrl)}
          altText={product.name}
          sourceSetProfile={sourceSetProfile}
          srcSize={168}
          className={classes.productImage}
        />

        {showProductInfo && (
          <Flex.Container
            className={classes.productCarouselProductContent}
            column
          >
            <ProductTile.Title className={productTitleClassName}>
              {product.name}
            </ProductTile.Title>

            <ProductTile.Price
              priceData={{
                currency: product.price.currency,
                current: product.price.current,
                previous: product.price.previous || {
                  text: "",
                  value: 0,
                },
                rrp: product.price.rrp || { text: "", value: 0 },
                isMarkedDown: product.price.isMarkedDown || false,
                isOutletPrice: product.price.isOutletPrice || false,
                discountPercentage: product.price.discountPercentage || 0,
              }}
              country={country}
              className={productPriceClassName}
              previousPriceCopy="Previous Price"
              currentPriceCopy="Current Price"
              currency={currency}
            />
          </Flex.Container>
        )}

        {overlayProductInfo && (
          <Box className={classes.overlay} paddingX="sm" asChild>
            <Flex.Container justify="center" align="center" column gap="xs">
              <ProductTile.Title maxLines={1} className={productTitleClassName}>
                {product.name}
              </ProductTile.Title>
              <ProductTile.Price
                priceData={{
                  currency: product.price.currency,
                  current: product.price.current,
                  previous: product.price.previous || {
                    text: "",
                    value: 0,
                  },
                  rrp: product.price.rrp || { text: "", value: 0 },
                  isMarkedDown: product.price.isMarkedDown || false,
                  isOutletPrice: product.price.isOutletPrice || false,
                  discountPercentage: product.price.discountPercentage || 0,
                }}
                country={country}
                className={productPriceClassName}
                previousPriceCopy="Previous Price"
                currentPriceCopy="Current Price"
                currency={currency}
              />
            </Flex.Container>
          </Box>
        )}
      </ProductLink>
    );

    return (
      <List.Item
        key={product.id}
        style={listItemStyle}
        className={classes.productCarouselItem}
      >
        <ProductTile.Root asChild>
          {Wrapper ? <Wrapper>{productLink}</Wrapper> : productLink}
        </ProductTile.Root>
      </List.Item>
    );
  });

  return listItems;
};
